/**
 * Debug logging helpers for the LedgerSync v3 bank-connection flow (DCP-216).
 *
 * These utilities are used exclusively for tracing the bank-connection lifecycle:
 * institution search, widget creation, OAuth redirect, callback polling, reconcile,
 * and disconnect.  They are the designated logging channel for all `lsDebug` /
 * `lsDebugError` call sites across `useLedgerSync`, `LedgerSyncCallbackPage`, and
 * `LedgerSyncConnectButton`.
 *
 * All log output is gated — silent by default in production builds — so it is
 * safe to ship.  Three independent conditions enable logging (any one is
 * sufficient):
 *
 *   1. `import.meta.env.DEV === true`  — always on during `pnpm dev`.
 *   2. `import.meta.env.VITE_LEDGERSYNC_DEBUG === 'true'`  — set at build time
 *      (e.g. a staging build or a local `.env.local` override).
 *   3. `localStorage.getItem('ls_debug') === '1'`  — toggled at runtime in the
 *      browser DevTools console without a redeploy:
 *
 *        localStorage.setItem('ls_debug', '1')   // enable
 *        localStorage.removeItem('ls_debug')      // disable
 *
 * The env-based conditions are evaluated once at module load and memoized.
 * The localStorage condition is re-checked on every call so it can be toggled
 * without a page reload.
 *
 * SECURITY: these functions must NEVER log JWTs, access tokens, or Authorization
 * headers — only ids and status values are acceptable.
 */

/** Memoized result of the build-time / env-based gate (parts 1 & 2). */
const ENV_ENABLED: boolean =
  import.meta.env.DEV === true || import.meta.env.VITE_LEDGERSYNC_DEBUG === 'true'

/**
 * Read the runtime localStorage toggle safely.
 * Wrapped in try/catch to avoid crashing in environments where localStorage is
 * unavailable (e.g. SSR, incognito storage-blocked contexts).
 *
 * @returns true when `ls_debug` is set to `'1'` in localStorage.
 */
function isLocalStorageEnabled(): boolean {
  try {
    return localStorage.getItem('ls_debug') === '1'
  } catch {
    return false
  }
}

/**
 * Determine whether LedgerSync debug logging is currently active.
 *
 * The env portion is memoized; localStorage is re-read each call so the flag
 * can be toggled at runtime via `localStorage.setItem('ls_debug', '1')` without
 * requiring a page reload.
 *
 * @returns true when any of the three gate conditions is met.
 */
export function lsDebugEnabled(): boolean {
  return ENV_ENABLED || isLocalStorageEnabled()
}

/**
 * Emit a `console.log` prefixed with `[ledgersync]` when debug logging is
 * enabled.  Safe to call unconditionally — a no-op in normal production use.
 *
 * @param step - Human-readable label identifying the flow step (e.g.
 *   `'request:create-widget'`).
 * @param data - Optional structured payload.  Must NOT contain secrets (tokens,
 *   auth headers).
 */
export function lsDebug(step: string, data?: unknown): void {
  if (!lsDebugEnabled()) return
  console.log('[ledgersync] ' + step, data ?? '')
}

/**
 * Emit a `console.error` prefixed with `[ledgersync]` when debug logging is
 * enabled.  Use for error paths so DevTools groups them under the red error
 * level.  Safe to call unconditionally — a no-op in normal production use.
 *
 * @param step - Human-readable label identifying the flow step.
 * @param data - Optional structured payload.  Must NOT contain secrets.
 */
export function lsDebugError(step: string, data?: unknown): void {
  if (!lsDebugEnabled()) return
  console.error('[ledgersync] ' + step, data ?? '')
}
